import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  Chip,
  IconButton,
  Button,
  DialogContent,
  DialogActions,
  Skeleton,
  Dialog,
  DialogTitle,
  Autocomplete,
  TextField,
  CircularProgress,
  Tooltip,
  Collapse,
  Drawer,
  Divider,
  Pagination,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import FilterListIcon from "@mui/icons-material/FilterList";
import CloseIcon from "@mui/icons-material/Close";
import CategoryIcon from "@mui/icons-material/Category";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import { useNavigate, useLocation } from "react-router-dom";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import PsychologyOutlinedIcon from "@mui/icons-material/PsychologyOutlined";
import MilitaryTechOutlinedIcon from "@mui/icons-material/MilitaryTechOutlined";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import "../../App.css";
import api from "../../api/client";
import { useSnackbar } from "../../providers/SnackbarProvider";
import { TableLoader } from "../../components/TableLoader";
import { TableNodata } from "../../components/TableNodata";
import RegenerateAnswerDialog from "../../components/RegenerateAnswerDialog";
import { useChangeableItems, getLabelByKey } from "../../store/useDynamicLabel";

// Green-styled markdown renderer for the answer card
const MarkdownAnswer = ({ text }) => (
  <ReactMarkdown
    remarkPlugins={[remarkGfm]}
    components={{
      p: ({ children }) => (
        <Typography sx={{ fontSize: "14px", color: "#1b5e20", lineHeight: 1.7, mb: 1 }}>{children}</Typography>
      ),
      strong: ({ children }) => (
        <Box component="strong" sx={{ fontWeight: 700, color: "#14532d" }}>{children}</Box>
      ),
      em: ({ children }) => <Box component="em" sx={{ fontStyle: "italic" }}>{children}</Box>,
      ul: ({ children }) => <Box component="ul" sx={{ pl: 3, mb: 1, mt: 0.5 }}>{children}</Box>,
      ol: ({ children }) => <Box component="ol" sx={{ pl: 3, mb: 1, mt: 0.5 }}>{children}</Box>,
      li: ({ children }) => (
        <Typography component="li" sx={{ fontSize: "14px", color: "#1b5e20", lineHeight: 1.7, mb: 0.5 }}>{children}</Typography>
      ),
      h1: ({ children }) => (
        <Typography sx={{ fontSize: "16px", fontWeight: 700, color: "#14532d", mt: 1.5, mb: 0.5 }}>{children}</Typography>
      ),
      h2: ({ children }) => (
        <Typography sx={{ fontSize: "15px", fontWeight: 700, color: "#14532d", mt: 1.5, mb: 0.5 }}>{children}</Typography>
      ),
      h3: ({ children }) => (
        <Typography sx={{ fontSize: "14px", fontWeight: 700, color: "#14532d", mt: 1.5, mb: 0.5 }}>{children}</Typography>
      ),
      code({ inline, children }) {
        const value = String(children);
        const isBlock = !inline && value.includes("\n");
        if (!isBlock) {
          return (
            <Box
              component="span"
              sx={{
                backgroundColor: "#c8e6c9",
                color: "#14532d",
                borderRadius: "6px",
                fontFamily: "'JetBrains Mono', 'Courier New', monospace",
                fontSize: "13px",
                px: 0.8,
                py: 0.2,
                mx: 0.3,
              }}
            >
              {value}
            </Box>
          );
        }
        return (
          <Box
            sx={{
              backgroundColor: "#1e293b",
              color: "#f1f5f9",
              p: 1.5,
              my: 1.5,
              borderRadius: "8px",
              overflowX: "auto",
              fontFamily: "'JetBrains Mono', 'Courier New', monospace",
              fontSize: "12px",
              border: "1px solid #334155",
            }}
          >
            <pre style={{ margin: 0 }}>
              <code>{value}</code>
            </pre>
          </Box>
        );
      },
    }}
  >
    {text}
  </ReactMarkdown>
);

// "View Answer" toggle + smoothly expanding answer card. When `canRegenerate`
// is true and a `quizId` is known, a regenerate IconButton appears next to the
// "Answer" label (same pattern AddCategory uses for generated questions).
const AnswerSection = ({
  answer,
  expanded,
  onToggle,
  hasOptions,
  quizId,
  canRegenerate = false,
  regenerating = false,
  onRegenerate,
}) => {
  if (!answer) return null;
  return (
    <Box sx={{ mt: hasOptions ? 3 : 2 }}>
      <Box
        onClick={onToggle}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.75,
          cursor: "pointer",
          userSelect: "none",
          color: "#2e7d32",
          fontWeight: 600,
          fontSize: "13px",
          px: 1.5,
          py: 0.75,
          borderRadius: "8px",
          border: "1px solid #a5d6a7",
          backgroundColor: "#f1f8f2",
          transition: "background-color 0.2s ease",
          "&:hover": { backgroundColor: "#e3f2e5" },
        }}
      >
        {expanded ? (
          <VisibilityOffOutlinedIcon sx={{ fontSize: "18px" }} />
        ) : (
          <VisibilityOutlinedIcon sx={{ fontSize: "18px" }} />
        )}
        {expanded ? "Hide Answer" : "View Answer"}
        <ExpandMoreIcon
          sx={{
            fontSize: "18px",
            transition: "transform 0.3s ease",
            transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
          }}
        />
      </Box>
      <Collapse in={expanded} timeout={300} unmountOnExit>
        <Box sx={{ mt: 2, p: 2.5, borderRadius: "12px", backgroundColor: "#e8f5e9", border: "1px solid #a5d6a7", position: "relative" }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, mb: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CheckCircleIcon sx={{ color: "#2e7d32", fontSize: "18px" }} />
              <Typography sx={{ fontWeight: 700, fontSize: "12px", color: "#2e7d32", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                Answer
              </Typography>
            </Box>
            {canRegenerate && quizId && (
              <Tooltip
                title={regenerating ? "Regenerating answer…" : "Regenerate this answer with a custom prompt"}
                arrow
                placement="left"
                componentsProps={{
                  tooltip: { sx: { bgcolor: "primary.main", color: "white", fontSize: 13, fontWeight: 500 } },
                  arrow: { sx: { color: "primary.main" } },
                }}
              >
                <span>
                  <IconButton
                    size="small"
                    disabled={regenerating}
                    onClick={onRegenerate}
                    sx={{
                      color: "#1c97e3",
                      backgroundColor: "#fff",
                      border: "1px solid #b6dcf7",
                      "&:hover": { backgroundColor: "#e7f3fd" },
                      "&.Mui-disabled": { color: "#1c97e3", backgroundColor: "#fff", opacity: 0.7 },
                    }}
                  >
                    {regenerating ? (
                      <CircularProgress size={16} sx={{ color: "#1c97e3" }} />
                    ) : (
                      <AutorenewIcon sx={{ fontSize: "18px" }} />
                    )}
                  </IconButton>
                </span>
              </Tooltip>
            )}
          </Box>
          <MarkdownAnswer text={answer} />
        </Box>
      </Collapse>
    </Box>
  );
};

// Difficulty chips inside cards keep their colour-coded palette
const getDifficultyColor = (level) => {
  const lvl = level?.toUpperCase();
  if (lvl === "BEGINNER") return { bg: "#edf7ed", text: "#4caf50" };
  if (lvl === "INTERMEDIATE") return { bg: "#fff4e5", text: "#ff9800" };
  if (lvl === "ADVANCED") return { bg: "#fdeded", text: "#f44336" };
  return { bg: "#e4eefb", text: "#1c97e3" };
};

// Fallback Bloom's levels when GetBloomsLevels is unavailable. The API
// accepts a single blooms_level per request and a comma-separated marks list.
const DEFAULT_BLOOMS_LEVELS = [
  "Remember",
  "Understand",
  "Apply",
  "Analyze",
  "Evaluate",
  "Create",
];

// Page size for both Accepted and Rejected — kept in sync with the API request.
const PAGE_SIZE = 15;

// Pulls difficulty / question-type out of the chip array on each question.
// Used only as a fallback for header metadata when the API response omits
// top-level `level` / `question_type` fields.
const KNOWN_DIFFICULTIES = ["Beginner", "Intermediate", "Advanced"];
const KNOWN_QUESTION_TYPES = ["MCQ", "Descriptive", "Fill in the blanks", "Programming-based", "Circuit Lab"];
const extractMeta = (item) => {
  const chips = (item?.chips || []).map((c) => String(c).toUpperCase());
  const difficulty = KNOWN_DIFFICULTIES.find((d) => chips.includes(d.toUpperCase())) || null;
  const questionType = KNOWN_QUESTION_TYPES.find((q) => chips.includes(q.toUpperCase())) || null;
  return { difficulty, questionType };
};

// Per-question card body — shared layout for both tabs. `canRegenerate` adds
// the inline "Regenerate this answer" IconButton next to the Answer label
// inside the expanded answer card (Accepted tab only).
const QuestionCard = ({
  item,
  idx,
  expanded,
  onToggleAnswer,
  mappedKey,
  onOpenMap,
  showMap,
  canRegenerate = false,
  regenerating = false,
  onRegenerate,
}) => (
  <Card
    sx={{
      p: { xs: 2.5, md: 3.5 },
      borderRadius: "14px",
      boxShadow: "0 1px 4px rgba(16,24,40,0.04)",
      border: "1px solid #eef2f7",
      transition: "box-shadow 0.25s ease, transform 0.25s ease, border-color 0.25s ease",
      "&:hover": {
        boxShadow: "0 10px 24px rgba(28,151,227,0.08)",
        borderColor: "#d8e9fb",
        transform: "translateY(-2px)",
      },
    }}
  >
    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, gap: 1, flexWrap: "wrap" }}>
      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
        {item.chips && item.chips.map((tag, tIdx) => {
          const isDifficulty = ["BEGINNER", "INTERMEDIATE", "ADVANCED"].includes(String(tag).toUpperCase());
          const colors = isDifficulty ? getDifficultyColor(tag) : { bg: "#e9f2fd", text: "#1c97e3" };
          return (
            <Chip key={tIdx} label={tag} size="small" sx={{ backgroundColor: colors.bg, color: colors.text, fontSize: "10px", fontWeight: 700, borderRadius: "6px" }} />
          );
        })}
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
        {item.blooms_level && (
          <Chip
            label={item.blooms_level}
            size="small"
            icon={<PsychologyOutlinedIcon sx={{ fontSize: "15px !important", color: "#5e35b1 !important" }} />}
            sx={{ fontSize: "11px", fontWeight: "bold", backgroundColor: "#ede7f6", color: "#5e35b1", borderRadius: "12px", letterSpacing: "0.5px" }}
          />
        )}
        {(item.marks !== undefined && item.marks !== null) && (
          <Chip
            label={`${item.marks} ${Number(item.marks) === 1 ? "Mark" : "Marks"}`}
            size="small"
            icon={<MilitaryTechOutlinedIcon sx={{ fontSize: "15px !important", color: "#e65100 !important" }} />}
            sx={{ fontSize: "11px", fontWeight: "bold", backgroundColor: "#fff3e0", color: "#e65100", borderRadius: "12px", letterSpacing: "0.5px" }}
          />
        )}
        <Typography sx={{ fontSize: "11px", color: "#8E9EBC", fontWeight: 600 }}>Quiz ID: {item.quiz_id}</Typography>
      </Box>
    </Box>

    <Typography sx={{ fontSize: "16px", fontWeight: 700, color: "#1c2434", mb: 3, lineHeight: 1.55 }}>
      {idx + 1}. {item.question}
    </Typography>

    {item.image_url && (
      <Box
        sx={{
          mb: 3,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          border: "1px solid #e0e0e0",
          borderRadius: "8px",
          p: 2,
          backgroundColor: "#fcfcfc",
          maxWidth: "100%",
        }}
      >
        <Box
          component="img"
          src={item.image_url}
          alt="Question Diagram / Circuit"
          sx={{ maxWidth: "100%", maxHeight: "350px", objectFit: "contain", borderRadius: "4px" }}
        />
      </Box>
    )}

    {item.feedback && (
      <Box sx={{ mb: 3, p: 2, backgroundColor: "#fff3cd", borderRadius: "8px", border: "1px solid #ffc107" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
          <Typography sx={{ fontWeight: 600, fontSize: "13px", color: "#856404" }}>Feedback:</Typography>
        </Box>
        <Typography sx={{ fontSize: "12px", color: "#666", mt: 0.5 }}>{item.feedback}</Typography>
      </Box>
    )}

    {item.options && item.options.length > 0 && (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mb: 1 }}>
        {item.options.map((opt, oIdx) => (
          <Typography key={oIdx} sx={{ fontSize: "14px", color: "#64748b" }}>
            {opt.label}. {opt.text}
          </Typography>
        ))}
      </Box>
    )}

    <AnswerSection
      answer={item.answer}
      hasOptions={item.options && item.options.length > 0}
      expanded={expanded}
      onToggle={onToggleAnswer}
      quizId={item.quiz_id}
      canRegenerate={canRegenerate}
      regenerating={regenerating}
      onRegenerate={onRegenerate}
    />

    {showMap && (
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 2 }}>
        {mappedKey ? (
          <Chip
            label={`Mapped: ${mappedKey}`}
            size="small"
            sx={{
              backgroundColor: "#d4edda",
              color: "#155724",
              fontWeight: 600,
              fontSize: "11px",
              borderRadius: "6px",
              maxWidth: "100%",
            }}
          />
        ) : (
          <Box />
        )}
        {/* Map Question hidden until mapping relevance validation is in place
        <Tooltip title={mappedKey ? "Re-Map Question" : "Map Question"} arrow>
          <IconButton
            size="small"
            onClick={onOpenMap}
            sx={{
              backgroundColor: mappedKey ? "#43a047" : "#1c97e3",
              color: "white",
              width: 36,
              height: 36,
              "&:hover": { backgroundColor: mappedKey ? "#388e3c" : "#157dbd" },
            }}
          >
            <CategoryIcon sx={{ fontSize: "20px" }} />
          </IconButton>
        </Tooltip>
        */}
      </Box>
    )}
  </Card>
);

const QuestionBankView = () => {

  const changeableItems = useChangeableItems();
  const getLabel = (itemKey) => getLabelByKey(changeableItems, itemKey);

  const navigate = useNavigate();
  const location = useLocation();
  const { showSnackbar } = useSnackbar();
  const [activeTab, setActiveTab] = useState("accepted");
  const [acceptedQuestions, setAcceptedQuestions] = useState([]);
  const [acceptedTotal, setAcceptedTotal] = useState(0);
  const [acceptedLoading, setAcceptedLoading] = useState(true);
  const [acceptedPage, setAcceptedPage] = useState(0);
  const [rejectedQuestions, setRejectedQuestions] = useState([]);
  const [rejectedTotal, setRejectedTotal] = useState(0);
  const [rejectedLoading, setRejectedLoading] = useState(true);
  const [rejectedPage, setRejectedPage] = useState(0);
  const [category, setCategory] = useState("");
  const [subcategory, setSubcategory] = useState("");
  const [description, setDescription] = useState("");
  const [headerLevel, setHeaderLevel] = useState("");
  const [headerQuestionType, setHeaderQuestionType] = useState("");
  const [expandedAnswers, setExpandedAnswers] = useState({});

  const toggleAnswer = (key) =>
    setExpandedAnswers((prev) => ({ ...prev, [key]: !prev[key] }));

  // ── More Filter state ────────────────────────────────────────────────
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  // Dropdown options for the drawer — Bloom's levels are fetched from the
  // backend (GetBloomsLevels) so the list matches what the API will accept.
  const [bloomsLevelOptions, setBloomsLevelOptions] = useState(DEFAULT_BLOOMS_LEVELS);

  // Selected = what's chosen in the drawer (not yet applied)
  const [selBloomsLevel, setSelBloomsLevel] = useState(null);
  // Marks is a comma-separated string (e.g. "2,5") so trainers can filter on
  // multiple values at once. Sent to the backend verbatim as `marks: "2,5"`.
  const [selMarks, setSelMarks] = useState("");

  // Applied = what's actually sent to the backend right now
  const [appBloomsLevel, setAppBloomsLevel] = useState(null);
  const [appMarks, setAppMarks] = useState("");

  // Map SubCategory Dialog State
  const [mapDialogOpen, setMapDialogOpen] = useState(false);
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [mapSubmitting, setMapSubmitting] = useState(false);
  const [mappedQuestions, setMappedQuestions] = useState({});

  // ── Per-answer Regenerate state (Accepted tab) ────────────────────────
  // Mirrors AddCategory: an inline IconButton on each expanded answer opens
  // the shared prompt dialog scoped to that single question.
  const [regenDialog, setRegenDialog] = useState({ open: false, quizId: null, question: "" });
  const [regeneratingAnswers, setRegeneratingAnswers] = useState({}); // { [quiz_id]: true }

  // Map dialog dropdown options
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [subCategoryOptions, setSubCategoryOptions] = useState([]);
  const levelOptions = ["Beginner", "Intermediate", "Advanced"];
  const [questionTypeOptions] = useState(["MCQ", "Descriptive", "Fill in the blanks", "Programming-based", "Circuit Lab"]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedSubCategory, setSelectedSubCategory] = useState(null);
  const [selectedLevel, setSelectedLevel] = useState(null);
  const [selectedQuestionType, setSelectedQuestionType] = useState(null);

  const commonTextFieldStyle = {
    "& .MuiOutlinedInput-root": {
      "& fieldset": { borderColor: "white" },
      "&:hover fieldset": { borderColor: "#d1d1d1" },
      "&.Mui-focused fieldset": { borderColor: "#1c97e3" },
    },
  };

  const fetchCategories = async () => {
    try {
      const res = await api.post("CategoryDropdown");
      setCategoryOptions(res?.data?.data || []);
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  const fetchSubCategories = async (categoryId) => {
    try {
      setSubCategoryOptions([]);
      setSelectedSubCategory(null);
      const res = await api.post("SubCategoryDropdown", { category_id: categoryId });
      setSubCategoryOptions(res?.data?.data || []);
    } catch (error) {
      console.error("Error fetching subcategories:", error);
    }
  };

  const handleOpenMapDialog = (item) => {
    setSelectedQuestion(item);
    setSelectedCategory(null);
    setSelectedSubCategory(null);
    setSelectedLevel(null);
    setSelectedQuestionType(null);
    setSubCategoryOptions([]);
    setMapDialogOpen(true);
    fetchCategories();
  };

  const handleCloseMapDialog = () => {
    setMapDialogOpen(false);
    setSelectedQuestion(null);
  };

  const handleMapSubmit = async () => {
    if (!selectedCategory || !selectedSubCategory || !selectedLevel || !selectedQuestionType) return;
    setMapSubmitting(true);
    try {
      const gen_id = `${selectedCategory?.category_id}_${selectedSubCategory?.subcategory_id}_${selectedLevel}_${selectedQuestionType}`;
      const payload = {
        id: selectedQuestion?.quiz_id,
        gen_id: gen_id,
        subcategory: selectedSubCategory?.subcategory_name,
        subcategory_id: selectedSubCategory?.subcategory_id,
      };
      const res = await api.post("MapQuestions", payload);
      if (res?.data?.message) {
        showSnackbar("success", res?.data?.message || "Question mapped successfully");
        setMappedQuestions((prev) => ({ ...prev, [selectedQuestion?.quiz_id]: gen_id }));
        handleCloseMapDialog();
      } else {
        showSnackbar("error", "Failed to map question");
      }
    } catch (error) {
      console.error("Error mapping question:", error);
      showSnackbar("error", error?.response?.data?.message || "An error occurred while mapping");
    } finally {
      setMapSubmitting(false);
    }
  };

  // Normalize a comma-separated marks input ("2, 5, abc, 10") down to a clean
  // wire string ("2,5,10") — drops blanks and anything non-numeric so the
  // backend never gets junk.
  const normalizeMarksInput = (raw) =>
    (raw || "")
      .split(",")
      .map((part) => part.trim())
      .filter((part) => part !== "" && !Number.isNaN(Number(part)))
      .join(",");

  const activeFilterCount = (appBloomsLevel ? 1 : 0) + (appMarks ? 1 : 0);
  const hasActiveFilters = activeFilterCount > 0;

  const openFilterDrawer = () => {
    // Re-sync drawer selections with currently-applied values
    setSelBloomsLevel(appBloomsLevel);
    setSelMarks(appMarks);
    setFilterDrawerOpen(true);
  };

  // Changing filters must reset BOTH tabs to page 0 — server-side pagination
  // means stale page indices would request out-of-range pages.
  const handleApplyFilters = () => {
    setAppBloomsLevel(selBloomsLevel);
    setAppMarks(normalizeMarksInput(selMarks));
    setAcceptedPage(0);
    setRejectedPage(0);
    setFilterDrawerOpen(false);
  };

  const handleClearAllFilters = () => {
    setAppBloomsLevel(null);
    setAppMarks("");
    setSelBloomsLevel(null);
    setSelMarks("");
    setAcceptedPage(0);
    setRejectedPage(0);
  };

  const removeBloomsChip = () => {
    setAppBloomsLevel(null);
    setSelBloomsLevel(null);
    setAcceptedPage(0);
    setRejectedPage(0);
  };
  const removeMarksChip = () => {
    setAppMarks("");
    setSelMarks("");
    setAcceptedPage(0);
    setRejectedPage(0);
  };

  // Open / close the per-answer regenerate prompt dialog.
  const openRegenDialog = (quizId, question) => {
    if (!quizId) {
      showSnackbar("error", "This question has no quiz_id.");
      return;
    }
    setRegenDialog({ open: true, quizId, question: question || "" });
  };

  const closeRegenDialog = () =>
    setRegenDialog({ open: false, quizId: null, question: "" });

  // Regenerate a single answer. Sends one quiz_id at a time so the inline
  // spinner stays accurate per question. On success patches the answer in
  // acceptedQuestions in-place (Option A — no extra fetch, no flicker).
  const regenerateAnswer = async (quizId, prompt) => {
    if (!quizId) return;
    setRegeneratingAnswers((prev) => ({ ...prev, [quizId]: true }));
    try {
      const payload = { quiz_ids: [quizId] };
      const trimmed = typeof prompt === "string" ? prompt.trim() : null;
      if (trimmed) payload.prompt = trimmed;
      const res = await api.post("RegenerateAnswers", payload);
      const data = res?.data;
      const updated = Array.isArray(data?.updated) ? data.updated : [];
      const match = updated.find((u) => u.quiz_id === quizId);
      if (data?.status === "success" && match) {
        setAcceptedQuestions((prev) =>
          prev.map((q) => (q.quiz_id === quizId ? { ...q, answer: match.answer ?? q.answer } : q))
        );
        // Auto-expand so the trainer sees the new answer immediately
        setExpandedAnswers((prev) => ({ ...prev, [quizId]: true }));
        showSnackbar("success", data.message || "Answer regenerated.");
      } else {
        showSnackbar(
          "error",
          data?.message || "Could not regenerate the answer. Please try again."
        );
      }
    } catch (error) {
      showSnackbar(
        "error",
        error?.response?.data?.message || error?.message || "Failed to regenerate the answer."
      );
    } finally {
      setRegeneratingAnswers((prev) => {
        const next = { ...prev };
        delete next[quizId];
        return next;
      });
    }
  };

  // Backend may not yet return top-level `level` / `question_type`; fall back
  // to deriving them from the first question's chips. All questions in a bank
  // share the same level + question_type (they're baked into gen_id), so safe.
  const applyMeta = (data, firstQuestion) => {
    setCategory(data?.category_name || "");
    setSubcategory(data?.subcategory_name || "");
    setDescription(data?.description || "");
    const derived = firstQuestion
      ? extractMeta(firstQuestion)
      : { difficulty: null, questionType: null };
    setHeaderLevel(data?.level || derived.difficulty || "");
    setHeaderQuestionType(data?.question_type || derived.questionType || "");
  };

  // Build the shared payload for both endpoints. Filters and page are appended
  // by the per-tab fetchers below since they're tab-specific (page) or shared.
  const buildBasePayload = () => {
    const genId = location.state?.assessment_id;
    if (!genId) return null;
    const payload = {
      gen_id: genId,
      category_id: location.state?.category_id || 0,
      subcategory_id: location.state?.subcategory_id || 0,
      size: PAGE_SIZE,
    };
    if (appBloomsLevel) payload.blooms_level = appBloomsLevel;
    // Comma-separated string (e.g. "2,5"). Backend splits on comma.
    if (appMarks) payload.marks = appMarks;
    return payload;
  };

  // Fetch one tab's worth of questions. Header metadata is refreshed from
  // whichever response lands first (per-call flag).
  const fetchAccepted = async (metaWriter) => {
    const base = buildBasePayload();
    if (!base) {
      setAcceptedLoading(false);
      return;
    }
    setAcceptedLoading(true);
    try {
      const res = await api.post("GetAcceptedQuestions", { ...base, page: acceptedPage });
      if (res?.data?.status === "success") {
        const questions = res?.data?.questions || [];
        setAcceptedQuestions(questions);
        setAcceptedTotal(res?.data?.total || 0);
        if (metaWriter) metaWriter(res.data, questions[0]);
      }
    } catch (error) {
      console.error("Error fetching accepted questions:", error);
    } finally {
      setAcceptedLoading(false);
    }
  };

  const fetchRejected = async (metaWriter) => {
    const base = buildBasePayload();
    if (!base) {
      setRejectedLoading(false);
      return;
    }
    setRejectedLoading(true);
    try {
      const res = await api.post("GetRejectedQuestions", { ...base, page: rejectedPage });
      if (res?.data?.status === "success") {
        const questions = res?.data?.questions || [];
        setRejectedQuestions(questions);
        setRejectedTotal(res?.data?.total || 0);
        if (metaWriter) metaWriter(res.data, questions[0]);
      }
    } catch (error) {
      console.error("Error fetching rejected questions:", error);
    } finally {
      setRejectedLoading(false);
    }
  };

  // Fetch Bloom's level options once on mount. Falls back silently to the
  // hardcoded DEFAULT_BLOOMS_LEVELS if the endpoint is unavailable.
  useEffect(() => {
    const fetchBloomsLevels = async () => {
      try {
        const res = await api.post("GetBloomsLevels", {});
        const list = res?.data?.data || [];
        const names = list
          .map((b) => b?.blooms_name)
          .filter((n) => typeof n === "string" && n.length > 0);
        if (names.length > 0) setBloomsLevelOptions(names);
      } catch (error) {
        // Keep the default list; not worth alerting the user.
        console.warn("GetBloomsLevels failed, using defaults:", error?.message);
      }
    };
    fetchBloomsLevels();
  }, []);

  // Accepted refetches on: page change, applied filter change, route state change.
  useEffect(() => {
    fetchAccepted(applyMeta);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state, acceptedPage, appBloomsLevel, appMarks]);

  // Rejected refetches independently. Header metadata is only written here
  // if accepted didn't supply it (rare — empty bank).
  useEffect(() => {
    fetchRejected((data, q) => {
      if (!category) applyMeta(data, q);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state, rejectedPage, appBloomsLevel, appMarks]);

  // ── Current tab's list, total, paging, loading ───────────────────────
  const isAccepted = activeTab === "accepted";
  const currentList = isAccepted ? acceptedQuestions : rejectedQuestions;
  const currentTotal = isAccepted ? acceptedTotal : rejectedTotal;
  const currentPage = isAccepted ? acceptedPage : rejectedPage;
  const setCurrentPage = isAccepted ? setAcceptedPage : setRejectedPage;
  const loading = isAccepted ? acceptedLoading : rejectedLoading;
  const currentVisible = currentList.length;
  const totalPages = Math.max(1, Math.ceil(currentTotal / PAGE_SIZE));

  return (
    <Box sx={{ minHeight: "100vh", p: { xs: 2, md: 4 }, backgroundColor: "#f7f9fc" }}>
      <Box sx={{ width: "100%", margin: "0 auto" }}>
        {/* ── Back ── */}
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(-1)}
          sx={{ mb: 1, color: "#1976d2", fontWeight: "bold", textTransform: "none" }}
        >
          Back
        </Button>

        {/* ── Hero header + meta strip ── */}
        <Box
          sx={{
            mb: 3,
            p: { xs: 2.5, md: 3 },
            borderRadius: "16px",
            background: "linear-gradient(135deg, #ffffff 0%, #f3f9ff 100%)",
            border: "1px solid #e3eaf2",
            boxShadow: "0 1px 4px rgba(16,24,40,0.04)",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
            <Box sx={{ backgroundColor: "#1c97e3", p: 0.8, borderRadius: "10px", display: "flex" }}>
              <CategoryIcon sx={{ color: "white", fontSize: "26px" }} />
            </Box>
            <Typography sx={{ fontSize: { xs: "22px", md: "28px" }, fontWeight: 800, color: "#1c2434" }}>
              {getLabel('QUESTION_BANK')} Details
            </Typography>
          </Box>

          {loading ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              <Skeleton variant="text" width="40%" height={24} />
              <Skeleton variant="text" width="70%" height={20} />
            </Box>
          ) : (
            <Box sx={{ pt: 0.5 }}>
              {/* Row 1: Subject + Subject Topics + Level + Question Type */}
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr 1fr", md: "1fr 1fr 1fr 1fr" },
                  gap: { xs: 2.5, md: 3 },
                  mb: 3,
                }}
              >
                <Box>
                  <Typography sx={{ fontSize: "13px", fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.6px", mb: 0.75 }}>
                    {getLabel('SUBJECT')}
                  </Typography>
                  <Typography sx={{ fontSize: "18px", fontWeight: 700, color: "#0f172a", lineHeight: 1.4 }}>
                    {category || "—"}
                  </Typography>
                </Box>
                <Box>
                  <Typography sx={{ fontSize: "13px", fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.6px", mb: 0.75 }}>
                    {getLabel('SUBJECT')} Topics
                  </Typography>
                  <Typography sx={{ fontSize: "17px", fontWeight: 600, color: "#1c2434", lineHeight: 1.4 }}>
                    {subcategory || "—"}
                  </Typography>
                </Box>
                <Box>
                  <Typography sx={{ fontSize: "13px", fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.6px", mb: 0.75 }}>
                    Level
                  </Typography>
                  {headerLevel ? (
                    <Chip
                      label={headerLevel}
                      size="small"
                      sx={{
                        backgroundColor: getDifficultyColor(headerLevel).bg,
                        color: getDifficultyColor(headerLevel).text,
                        fontSize: "13px",
                        fontWeight: 700,
                        borderRadius: "6px",
                        height: "28px",
                        px: 0.5,
                      }}
                    />
                  ) : (
                    <Typography sx={{ fontSize: "17px", fontWeight: 600, color: "#1c2434" }}>—</Typography>
                  )}
                </Box>
                <Box>
                  <Typography sx={{ fontSize: "13px", fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.6px", mb: 0.75 }}>
                    Question Type
                  </Typography>
                  {headerQuestionType ? (
                    <Chip
                      label={headerQuestionType}
                      size="small"
                      sx={{
                        backgroundColor: "#e9f2fd",
                        color: "#1c97e3",
                        fontSize: "13px",
                        fontWeight: 700,
                        borderRadius: "6px",
                        height: "28px",
                        px: 0.5,
                      }}
                    />
                  ) : (
                    <Typography sx={{ fontSize: "17px", fontWeight: 600, color: "#1c2434" }}>—</Typography>
                  )}
                </Box>
              </Box>
              {/* Row 2: Description — full width so multi-line text wraps cleanly */}
              <Box>
                <Typography sx={{ fontSize: "13px", fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.6px", mb: 0.75 }}>
                  Description
                </Typography>
                <Typography sx={{ fontSize: "15px", color: "#334155", lineHeight: 1.7 }}>
                  {description || "—"}
                </Typography>
              </Box>
            </Box>
          )}
        </Box>

        {/* ── Compact tab toggle (pill style) ── */}
        <Box
          sx={{
            display: "inline-flex",
            p: 0.5,
            borderRadius: "12px",
            backgroundColor: "#e9eef5",
            border: "1px solid #e3eaf2",
            mb: 3,
            gap: 0.5,
          }}
        >
          {[
            { key: "accepted", label: "Accepted", count: acceptedTotal, tabLoading: acceptedLoading, icon: <CheckCircleOutlineIcon sx={{ fontSize: "18px" }} />, activeColor: "#2e7d32", activeBg: "#e8f5e9" },
            { key: "rejected", label: "Rejected", count: rejectedTotal, tabLoading: rejectedLoading, icon: <CancelOutlinedIcon sx={{ fontSize: "18px" }} />, activeColor: "#c62828", activeBg: "#fdecea" },
          ].map((tab) => {
            const isActive = activeTab === tab.key;
            return (
              <Box
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  cursor: "pointer",
                  userSelect: "none",
                  px: 2,
                  py: 1,
                  borderRadius: "10px",
                  backgroundColor: isActive ? "#ffffff" : "transparent",
                  color: isActive ? tab.activeColor : "#64748b",
                  fontWeight: 700,
                  fontSize: "14px",
                  boxShadow: isActive ? "0 1px 3px rgba(16,24,40,0.08)" : "none",
                  transition: "all 0.2s ease",
                  "&:hover": { backgroundColor: isActive ? "#ffffff" : "#f3f6fa" },
                }}
              >
                {tab.icon}
                {tab.label}
                <Chip
                  label={tab.tabLoading ? "…" : tab.count}
                  size="small"
                  sx={{
                    height: "20px",
                    fontSize: "11px",
                    fontWeight: 700,
                    backgroundColor: isActive ? tab.activeBg : "#dde4ed",
                    color: isActive ? tab.activeColor : "#475569",
                  }}
                />
              </Box>
            );
          })}
        </Box>

        {/* ── Filter toolbar (matches QuestionPaperBuilder pattern) ── */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
            mb: 2.5,
          }}
        >
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            {appBloomsLevel && (
              <Box className="filter-chip">
                <Typography sx={{ fontSize: "13px", fontWeight: 500 }}>{appBloomsLevel}</Typography>
                <IconButton size="small" onClick={removeBloomsChip} sx={{ ml: 0.5 }}>
                  <CloseIcon sx={{ fontSize: "14px" }} />
                </IconButton>
              </Box>
            )}
            {appMarks && (
              <Box className="filter-chip">
                <Typography sx={{ fontSize: "13px", fontWeight: 500 }}>
                  {`Marks: ${appMarks.split(",").join(", ")}`}
                </Typography>
                <IconButton size="small" onClick={removeMarksChip} sx={{ ml: 0.5 }}>
                  <CloseIcon sx={{ fontSize: "14px" }} />
                </IconButton>
              </Box>
            )}
          </Box>

          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            <Typography sx={{ fontSize: "13px", color: "#475569", fontWeight: 600, mr: 1 }}>
              Showing <Box component="span" sx={{ color: "#1c97e3", fontWeight: 700 }}>{loading ? "…" : currentVisible}</Box>
              {" "}of {loading ? "…" : currentTotal}
            </Typography>
            <IconButton onClick={openFilterDrawer} color="primary" className="more-filter-btn">
              <FilterListIcon />
              <Typography className="more-filter-text">More filter</Typography>
            </IconButton>
            {hasActiveFilters && (
              <Button variant="outlined" className="clear-filter-btn" onClick={handleClearAllFilters}>
                Clear Filter
              </Button>
            )}
          </Box>
        </Box>

        {/* ── Questions list ── */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
          {loading ? (
            <Box sx={{ py: 8 }}>
              <TableLoader message="Fetching questions..." />
            </Box>
          ) : currentList.length === 0 ? (
            <Box sx={{ py: 5, width: "100%" }}>
              <TableNodata />
              {hasActiveFilters && (
                <Box sx={{ textAlign: "center", mt: 2 }}>
                  <Typography sx={{ fontSize: "13px", color: "#64748b", mb: 1 }}>
                    No {activeTab} questions match the current filters.
                  </Typography>
                  <Button
                    size="small"
                    onClick={handleClearAllFilters}
                    sx={{ textTransform: "none", fontWeight: 600, color: "#1c97e3" }}
                  >
                    Clear filters
                  </Button>
                </Box>
              )}
            </Box>
          ) : (
            currentList.map((item, idx) => (
              <QuestionCard
                key={item.quiz_id || idx}
                item={item}
                idx={idx + currentPage * PAGE_SIZE}
                expanded={!!expandedAnswers[item.quiz_id ?? idx]}
                onToggleAnswer={() => toggleAnswer(item.quiz_id ?? idx)}
                mappedKey={mappedQuestions[item.quiz_id]}
                onOpenMap={() => handleOpenMapDialog(item)}
                showMap={isAccepted}
                canRegenerate={isAccepted}
                regenerating={!!regeneratingAnswers[item.quiz_id]}
                onRegenerate={() => openRegenDialog(item.quiz_id, item.question)}
              />
            ))
          )}
        </Box>

        {/* ── Pagination (Previous + numbered pages + Next) ── */}
        {!loading && currentTotal > PAGE_SIZE && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mt: 3,
              p: 1.5,
              borderRadius: "12px",
              backgroundColor: "#ffffff",
              border: "1px solid #eef2f7",
            }}
          >
            {currentPage !== 0 ? (
              <Button
                variant="outlined"
                startIcon={<ArrowBackIcon sx={{ color: "#414651" }} />}
                onClick={() => setCurrentPage(currentPage - 1)}
                sx={{
                  borderRadius: "12px",
                  textTransform: "none",
                  color: "#414651",
                  borderColor: "#d0d0d0",
                }}
              >
                Previous
              </Button>
            ) : (
              <Box sx={{ width: "110px" }} />
            )}

            <Pagination
              page={currentPage + 1}
              onChange={(_, value) => setCurrentPage(value - 1)}
              count={totalPages}
              siblingCount={1}
              boundaryCount={1}
              shape="rounded"
              hidePrevButton
              hideNextButton
            />

            {currentPage + 1 !== totalPages ? (
              <Button
                variant="outlined"
                endIcon={<ArrowForwardIcon sx={{ color: "#414651" }} />}
                onClick={() => setCurrentPage(currentPage + 1)}
                sx={{
                  borderRadius: "12px",
                  textTransform: "none",
                  color: "#414651",
                  borderColor: "#d0d0d0",
                }}
              >
                Next
              </Button>
            ) : (
              <Box sx={{ width: "110px" }} />
            )}
          </Box>
        )}
      </Box>

      {/* ── More Filter Drawer (matches QuestionPaperBuilder pattern) ── */}
      <Drawer
        anchor="right"
        open={filterDrawerOpen}
        onClose={() => setFilterDrawerOpen(false)}
        PaperProps={{
          sx: {
            width: 320,
            p: 3,
            display: "flex",
            flexDirection: "column",
            gap: 2.5,
            height: "100vh",
            borderRadius: "12px 0 0 12px",
          },
        }}
      >
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1}>
            <FilterListIcon color="primary" />
            <Typography variant="h6" fontWeight={600} color="primary">
              Filters
            </Typography>
          </Box>
          <IconButton aria-label="Close filters" size="small" onClick={() => setFilterDrawerOpen(false)}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Divider />

        {/* Bloom's Taxonomy Level — the API accepts a single value per request */}
        <Box display="flex" flexDirection="column" gap={0.8}>
          <Typography variant="subtitle2" fontWeight={600}>
            Bloom's Level
          </Typography>
          <Autocomplete
            size="small"
            options={bloomsLevelOptions}
            value={selBloomsLevel}
            onChange={(event, newValue) => setSelBloomsLevel(newValue)}
            renderInput={(params) => (
              <TextField {...params} placeholder="All Bloom's Levels" fullWidth variant="outlined" />
            )}
          />
        </Box>

        {/* Marks — accepts a comma-separated list (e.g. "2,5") and is sent
            to the backend verbatim. Non-numeric tokens are stripped on Apply. */}
        <Box display="flex" flexDirection="column" gap={0.8}>
          <Typography variant="subtitle2" fontWeight={600}>
            Marks
          </Typography>
          <TextField
            size="small"
            fullWidth
            variant="outlined"
            placeholder="e.g. 2,5"
            value={selMarks}
            onChange={(event) => setSelMarks(event.target.value)}
            helperText="Separate multiple values with commas"
            FormHelperTextProps={{ sx: { ml: 0, mt: 0.5, fontSize: "11px", color: "#94a3b8" } }}
          />
        </Box>

        <Button
          variant="contained"
          color="primary"
          fullWidth
          className="customButton"
          onClick={handleApplyFilters}
          sx={{ mt: 2 }}
        >
          Apply Filters
        </Button>
      </Drawer>

      {/* ── Map SubCategory Dialog ── */}
      <Dialog open={mapDialogOpen} onClose={handleCloseMapDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Box sx={{ backgroundColor: "#1c97e3", p: 0.6, borderRadius: "8px", display: "flex" }}>
                <CategoryIcon sx={{ color: "white", fontSize: "22px" }} />
              </Box>
              <Box>
                <Typography sx={{ fontSize: "18px", fontWeight: 700, color: "#1c2434" }}>
                  Map Question
                </Typography>
                {selectedQuestion && (
                  <Typography sx={{ fontSize: "12px", color: "#8E9EBC", fontWeight: 500 }}>
                    Quiz ID: {selectedQuestion.quiz_id}
                  </Typography>
                )}
              </Box>
            </Box>
            <IconButton size="small" onClick={handleCloseMapDialog}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ pt: 1 }}>
          <Box sx={{ display: "flex", flexDirection: "column", rowGap: 2.5, pt: 1 }}>
            <Grid sx={{ width: "100%" }}>
              <Typography sx={{ mb: 0.8, fontWeight: 600, fontSize: "14px", color: "#1c2434" }}>
                {`${getLabel('SUBJECT')}`} <span style={{ color: "red" }}>*</span>
              </Typography>
              <Autocomplete
                options={categoryOptions}
                getOptionLabel={(option) => option?.category_name || ""}
                isOptionEqualToValue={(option, value) => option.category_id === value?.category_id}
                value={selectedCategory}
                onChange={(_, newValue) => {
                  setSelectedCategory(newValue);
                  if (newValue?.category_id) fetchSubCategories(newValue.category_id);
                  else { setSubCategoryOptions([]); setSelectedSubCategory(null); }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Select subject"
                    sx={{ ...commonTextFieldStyle, backgroundColor: "#f8f9fa", borderRadius: "8px" }}
                  />
                )}
              />
            </Grid>

            <Grid sx={{ width: "100%" }}>
              <Typography sx={{ mb: 0.8, fontWeight: 600, fontSize: "14px", color: "#1c2434" }}>
                Subtopic <span style={{ color: "red" }}>*</span>
              </Typography>
              <Autocomplete
                options={subCategoryOptions}
                getOptionLabel={(option) => option?.subcategory_name || ""}
                isOptionEqualToValue={(option, value) => option.subcategory_id === value?.subcategory_id}
                value={selectedSubCategory}
                disabled={!selectedCategory}
                onChange={(_, newValue) => setSelectedSubCategory(newValue)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder={selectedCategory ? "Select subtopic" : "Select a subject first"}
                    sx={{ ...commonTextFieldStyle, backgroundColor: !selectedCategory ? "#f1f1f1" : "#f8f9fa", borderRadius: "8px" }}
                  />
                )}
              />
            </Grid>

            <Grid sx={{ width: "100%" }}>
              <Typography sx={{ mb: 0.8, fontWeight: 600, fontSize: "14px", color: "#1c2434" }}>
                Level <span style={{ color: "red" }}>*</span>
              </Typography>
              <Autocomplete
                options={levelOptions}
                value={selectedLevel}
                onChange={(_, newValue) => setSelectedLevel(newValue)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Select level"
                    sx={{ ...commonTextFieldStyle, backgroundColor: "#f8f9fa", borderRadius: "8px" }}
                  />
                )}
              />
            </Grid>

            <Grid sx={{ width: "100%" }}>
              <Typography sx={{ mb: 0.8, fontWeight: 600, fontSize: "14px", color: "#1c2434" }}>
                Question Type <span style={{ color: "red" }}>*</span>
              </Typography>
              <Autocomplete
                options={questionTypeOptions}
                value={selectedQuestionType}
                onChange={(_, newValue) => setSelectedQuestionType(newValue)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Select question type"
                    sx={{ ...commonTextFieldStyle, backgroundColor: "#f8f9fa", borderRadius: "8px" }}
                  />
                )}
              />
            </Grid>
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3, pt: 1 }}>
          <Button
            variant="outlined"
            color="secondary"
            onClick={handleCloseMapDialog}
            sx={{ textTransform: "none", borderRadius: "12px", px: 3 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={mapSubmitting || !selectedCategory || !selectedSubCategory || !selectedLevel || !selectedQuestionType}
            onClick={handleMapSubmit}
            sx={{
              textTransform: "none",
              borderRadius: "12px",
              px: 4,
              fontWeight: 600,
              backgroundColor: "#1c97e3",
              "&:hover": { backgroundColor: "#157dbd" },
            }}
          >
            {mapSubmitting ? <CircularProgress size={20} color="inherit" /> : "Map Question"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Regenerate Answer Prompt Dialog ──
          Opened from the per-answer IconButton inside each expanded answer.
          The shared dialog owns its preset/custom state; we just hand it
          the resolved prompt and call /RegenerateAnswers with one quiz_id. */}
      <RegenerateAnswerDialog
        open={regenDialog.open}
        onClose={closeRegenDialog}
        loading={!!regeneratingAnswers[regenDialog.quizId]}
        targetSummary={regenDialog.question || null}
        onConfirm={(prompt) => {
          const { quizId } = regenDialog;
          closeRegenDialog();
          regenerateAnswer(quizId, prompt);
        }}
      />
    </Box>
  );
};

export default QuestionBankView;
